import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GitBranch, ListChecks, Loader2, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';
import { cn } from '../lib/cn';
import { useNotifications } from '../store/useStore';
import { MailingIntestazione } from '../components/email/MailingIntestazione';
import { ListaDialog } from '../components/email/liste/ListaDialog';
import { DettaglioLista } from '../components/email/liste/DettaglioLista';
import { AggiungiContattiDialog } from '../components/email/liste/AggiungiContattiDialog';
import { listeService } from '../lib/email/listeService';
import { automationService } from '../lib/email/automationService';
import { esportaListaExcel } from '../lib/email/esportaLista';
import { sequenzeCheParteDa } from '../lib/email/sequenzeDellaLista';
import type { EmailLista } from '../types/email.types';
import type { EmailAutomation } from '../types/automazioni.types';

/** Gli errori del database non sono Error ma oggetti con un messaggio: vanno letti anche quelli. */
function messaggioErrore(errore: unknown): string {
  if (errore instanceof Error) return errore.message;
  if (errore && typeof errore === 'object' && 'message' in errore) return String(errore.message);
  return 'Errore imprevisto.';
}

const contatti = (n: number) => `${n} ${n === 1 ? 'contatto' : 'contatti'}`;

/**
 * La scheda Liste: a sinistra le liste, a destra quella aperta. Si crea una
 * lista, si preme "Aggiungi contatti" e si spunta chi metterci.
 *
 * La lista aperta sta nell'indirizzo (?lista=...): tornando indietro o
 * ricaricando si ritrova quella di prima.
 */
export default function EmailListePage() {
  const { addNotification } = useNotifications();
  const [parametri, setParametri] = useSearchParams();

  const [liste, setListe] = useState<EmailLista[]>([]);
  /** Se le liste non si leggono si dice, invece di mostrare "nessuna lista". */
  const [erroreListe, setErroreListe] = useState<string | null>(null);
  const [automazioni, setAutomazioni] = useState<EmailAutomation[]>([]);
  const [caricando, setCaricando] = useState(true);
  /** Sale a ogni aggiunta o tolta: il riquadro della lista rilegge i contatti. */
  const [versione, setVersione] = useState(0);

  const [listaInModifica, setListaInModifica] = useState<{ aperta: boolean; lista: EmailLista | null }>({
    aperta: false,
    lista: null,
  });
  const [listaDaEliminare, setListaDaEliminare] = useState<EmailLista | null>(null);
  const [eliminando, setEliminando] = useState(false);
  /** La lista in cui si stanno aggiungendo contatti, per id: mai "la prima che capita". */
  const [daRiempire, setDaRiempire] = useState<string | null>(null);
  const [esportando, setEsportando] = useState(false);

  /** Rilegge le liste. Torna falso se non ci riesce. */
  const caricaListe = useCallback(async (): Promise<boolean> => {
    try {
      setListe(await listeService.elenco());
      setErroreListe(null);
      return true;
    } catch (errore) {
      setErroreListe(messaggioErrore(errore));
      return false;
    }
  }, []);

  const caricaTutto = useCallback(async () => {
    setCaricando(true);
    // Le sequenze servono solo ad avvisare "questa lista fa partire una
    // sequenza". Chi non puo' vederle vede le liste lo stesso, senza avviso.
    const sequenze = automationService.elenco().catch(() => [] as EmailAutomation[]);
    await Promise.all([caricaListe(), sequenze.then(setAutomazioni)]);
    setCaricando(false);
  }, [caricaListe]);

  useEffect(() => {
    caricaTutto();
  }, [caricaTutto]);

  // Se l'indirizzo non dice quale lista, o dice una che non c'e' piu', si apre la prima.
  const richiesta = parametri.get('lista');
  const aperta = liste.find((l) => l.id === richiesta) ?? liste[0] ?? null;

  const apri = useCallback(
    (id: string | null) => setParametri(id ? { lista: id } : {}, { replace: true }),
    [setParametri]
  );

  const sequenzeDi = useCallback((id: string) => sequenzeCheParteDa(automazioni, id), [automazioni]);
  const sequenzeAperta = useMemo(() => (aperta ? sequenzeDi(aperta.id) : []), [aperta, sequenzeDi]);
  const listaDaRiempire = liste.find((l) => l.id === daRiempire) ?? null;
  const sequenzeDaRiempire = useMemo(
    () => (daRiempire ? sequenzeDi(daRiempire) : []),
    [daRiempire, sequenzeDi]
  );

  // --- Creare, rinominare, eliminare ---------------------------------------

  const salvaLista = useCallback(
    async (nome: string, descrizione: string) => {
      const inModifica = listaInModifica.lista;
      try {
        let nuovaId: string | null = null;
        if (inModifica) await listeService.modifica(inModifica.id, nome, descrizione);
        else nuovaId = await listeService.crea(nome, descrizione);
        setListaInModifica({ aperta: false, lista: null });
        const riletta = await caricaListe();
        // Appena creata si apre, e si passa subito a sceglierne i contatti.
        // La finestra cerca la lista per id: se la rilettura e' fallita non
        // si apre, invece di aprirsi su un'altra lista.
        if (nuovaId) {
          apri(nuovaId);
          if (riletta) setDaRiempire(nuovaId);
        }
      } catch (errore) {
        addNotification({ type: 'error', title: 'Non riesco a salvare la lista', message: messaggioErrore(errore) });
      }
    },
    [listaInModifica.lista, apri, caricaListe, addNotification]
  );

  const eliminaLista = useCallback(async () => {
    if (!listaDaEliminare) return;
    setEliminando(true);
    try {
      const eliminata = listaDaEliminare.id;
      await listeService.elimina(eliminata);
      // Sparisce subito dall'elenco: non resta aperta, nemmeno per un attimo.
      setListe((prima) => prima.filter((l) => l.id !== eliminata));
      setListaDaEliminare(null);
      apri(null);
      await caricaListe();
    } catch (errore) {
      addNotification({ type: 'error', title: 'Non riesco a eliminare la lista', message: messaggioErrore(errore) });
    } finally {
      setEliminando(false);
    }
  }, [listaDaEliminare, apri, caricaListe, addNotification]);

  // --- Mettere e togliere contatti -----------------------------------------

  const aggiungi = useCallback(
    async (ids: string[]) => {
      if (!listaDaRiempire) return;
      try {
        await listeService.aggiungi(listaDaRiempire.id, ids);
        addNotification({
          type: 'success',
          title: 'Aggiunti alla lista',
          message: `${contatti(ids.length)} in "${listaDaRiempire.name}".`,
        });
        setDaRiempire(null);
        setVersione((v) => v + 1);
        await caricaListe();
      } catch (errore) {
        addNotification({ type: 'error', title: 'Non riesco ad aggiungerli', message: messaggioErrore(errore) });
      }
    },
    [listaDaRiempire, caricaListe, addNotification]
  );

  const togli = useCallback(
    async (ids: string[]) => {
      if (!aperta) return;
      try {
        await listeService.togli(aperta.id, ids);
        addNotification({
          type: 'success',
          title: 'Tolti dalla lista',
          message: `${contatti(ids.length)} fuori da "${aperta.name}". Restano in rubrica.`,
        });
        setVersione((v) => v + 1);
        await caricaListe();
      } catch (errore) {
        addNotification({ type: 'error', title: 'Non riesco a toglierli', message: messaggioErrore(errore) });
      }
    },
    [aperta, caricaListe, addNotification]
  );

  const esporta = useCallback(async () => {
    if (!aperta) return;
    setEsportando(true);
    try {
      const quanti = await esportaListaExcel(aperta, liste);
      addNotification({
        type: 'success',
        title: 'Excel pronto',
        message: `${contatti(quanti)} della lista "${aperta.name}" scaricati.`,
      });
    } catch (errore) {
      addNotification({ type: 'error', title: 'Esportazione non riuscita', message: messaggioErrore(errore) });
    } finally {
      setEsportando(false);
    }
  }, [aperta, liste, addNotification]);

  const nuova = () => setListaInModifica({ aperta: true, lista: null });

  return (
    <div className="space-y-4 md:space-y-6">
      <MailingIntestazione descrizione="Gruppi di contatti a cui mandare una campagna o far partire una sequenza.">
        <Button
          onClick={nuova}
          className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova lista
        </Button>
      </MailingIntestazione>

      {caricando ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : erroreListe && liste.length === 0 ? (
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-red-700 font-medium">Non riesco a leggere le liste</p>
            <p className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">{erroreListe}</p>
            <Button variant="outline" className="mt-4" onClick={caricaTutto}>
              Riprova
            </Button>
          </CardContent>
        </Card>
      ) : liste.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-14 text-center">
            <ListChecks className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
            <p className="text-sm text-neutral-600 font-medium">Nessuna lista ancora</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-md mx-auto leading-relaxed">
              Una lista raggruppa dei contatti: i clienti di un corso, chi hai conosciuto a una fiera.
              Creala, poi scegli dalla rubrica chi metterci.
            </p>
            <Button className="mt-4" onClick={nuova}>
              <Plus className="w-4 h-4 mr-2" />
              Crea la prima lista
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {erroreListe && (
            <p className="flex flex-wrap items-center gap-2 text-xs text-red-700">
              Non riesco ad aggiornare le liste: i numeri potrebbero essere vecchi.
              <button type="button" onClick={caricaTutto} className="underline underline-offset-2">
                Riprova
              </button>
            </p>
          )}
          <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)] items-start">
            <nav aria-label="Liste" className="space-y-1.5">
              {liste.map((l) => {
                const scelta = aperta?.id === l.id;
                const conSequenza = sequenzeDi(l.id).length > 0;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => apri(l.id)}
                    aria-current={scelta ? 'true' : undefined}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg border bg-white transition-colors',
                      scelta ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-gray-900 truncate">{l.name}</span>
                      {conSequenza && (
                        <GitBranch
                          className="w-3.5 h-3.5 text-primary-500 flex-shrink-0"
                          aria-label="Fa partire una sequenza"
                        />
                      )}
                    </span>
                    <span className="block text-[11px] text-neutral-500 mt-0.5">
                      {contatti(l.contatti)} ·{' '}
                      <span className="text-green-700">{l.possono_ricevere} possono ricevere</span>
                    </span>
                  </button>
                );
              })}
            </nav>

            {aperta && (
              <DettaglioLista
                key={aperta.id}
                lista={aperta}
                sequenze={sequenzeAperta}
                versione={versione}
                esportando={esportando}
                onAggiungi={() => setDaRiempire(aperta.id)}
                onTogli={togli}
                onEsporta={esporta}
                onRinomina={() => setListaInModifica({ aperta: true, lista: aperta })}
                onElimina={() => setListaDaEliminare(aperta)}
              />
            )}
          </div>
        </div>
      )}

      <ListaDialog
        aperta={listaInModifica.aperta}
        lista={listaInModifica.lista}
        onChiudi={() => setListaInModifica({ aperta: false, lista: null })}
        onSalva={salvaLista}
      />

      <AggiungiContattiDialog
        aperta={Boolean(listaDaRiempire)}
        lista={listaDaRiempire}
        sequenze={sequenzeDaRiempire}
        onChiudi={() => setDaRiempire(null)}
        onConferma={aggiungi}
      />

      <ConfirmDeleteDialog
        open={Boolean(listaDaEliminare)}
        onOpenChange={(v) => !v && setListaDaEliminare(null)}
        onConfirm={eliminaLista}
        isDeleting={eliminando}
        title={`Eliminare la lista "${listaDaEliminare?.name ?? ''}"?`}
        description="I contatti restano in rubrica: si toglie solo il raggruppamento. Le campagne gia' partite non cambiano."
        confirmLabel="Elimina la lista"
      />
    </div>
  );
}
